import { logger } from '@adornis/base/logging.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { AsyncLocalStorage } from 'node:async_hooks';
import { getSubject } from './subject.js';

/**
 * File documents carry no owner in @adornis/file-utils. Ownership is stamped into two raw fields right after a
 * file is created — undeclared on the entity on purpose: baseql drops undeclared fields from client input, so a
 * client can never set or change them, and every reader of ownership (policies.ts, the serve route) reads the
 * raw document anyway.
 *
 *  - `ownerUserID`   — the AdornisUser `_id` or `guest:` id that owns the file.
 *  - `readerUserIDs` — additional users allowed to read it (students the file was released to).
 *
 * The owner is the caller in a client context. Background jobs run in server context with no caller, so they
 * declare the owner (the exam owner) with `runWithFileOwner` — see jobs/runner.ts.
 */

/**
 * Constructed on first use, not at module scope: this module is reached from the client bundle, where
 * `node:async_hooks` is shimmed to `undefined` — see the same note in jobs/job-signal.ts.
 */
let fileOwnerScope: AsyncLocalStorage<string> | undefined = undefined;

export function runWithFileOwner<T>(ownerUserID: string | null | undefined, fn: () => T): T {
  if (!ownerUserID) return fn();
  fileOwnerScope ??= new AsyncLocalStorage<string>();
  return fileOwnerScope.run(ownerUserID, fn);
}

async function resolveOwner(): Promise<string | undefined> {
  const scoped = fileOwnerScope?.getStore();
  if (scoped) return scoped;
  const subject = await getSubject();
  return subject.userID ?? subject.guestID;
}

/** Wraps `afterCreate` of the file base class; ImageFile and PDFFile inherit it (neither overrides it). */
export function installFileOwnerStamping() {
  const original = AdornisFile.afterCreate.bind(AdornisFile);
  AdornisFile.afterCreate = async function (entity: any, result: any) {
    await original(entity, result);
    const ownerUserID = await resolveOwner();
    if (!ownerUserID) {
      // Unowned files are unreadable by any client (policies.ts) — log so a missing scope is noticed.
      logger.warn({ context: 'installFileOwnerStamping', fileID: entity?._id, _class: entity?._class }, 'File created without owner');
      return;
    }
    await (await getRawCollection(AdornisFile._collectionName)).updateOne({ _id: entity._id }, { $set: { ownerUserID } });
  } as typeof AdornisFile.afterCreate;
}

/** Grants (or, with `grant: false`, revokes) read access on files to the given users — result release. */
export async function setFileReaders(fileIDs: string[], userIDs: string[], grant: boolean) {
  const ids = fileIDs.filter(Boolean);
  if (!ids.length || !userIDs.length) return;
  const files = await getRawCollection(AdornisFile._collectionName);
  const filter = { _id: { $in: ids as any[] } };
  if (grant) await files.updateMany(filter, { $addToSet: { readerUserIDs: { $each: userIDs } } });
  else await files.updateMany(filter, { $pull: { readerUserIDs: { $in: userIDs } } as any });
}
